<template>
  <v-container class="insurance-assets-and-liabilities-container">
    <v-row no-gutters>
      <v-col cols="12" sm="1">
        <router-link :to="{ name: 'InsuranceExplanation' }" class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
      <v-col cols="12" sm="11">
        <h1 class="mb-2 title-section">
          Tell us about the values of your assets and liabilities
        </h1>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12" md="4" offset-md="1" class="form-blurb"> </v-col>

      <v-col cols="12" md="7">
        <div class="left-section-container">
          <ValidationObserver ref="observer">
            <div class="insurance-asset-block">
              <div v-if="marriedInCommunityOrCustomary">
                <h3 class="mb-2">Gross value of the combined estate</h3>
                <p>
                  When you are married in community of property, you and your
                  spouse have a joint estate and therefore jointly own all your
                  assets - irrespective of when they were acquired. Please give
                  an account of all the assets that you and your spouse own.
                </p>
                <h4>
                  What is the current value of properties you and your spouse
                  own?
                </h4>
              </div>
              <div v-else>
                <h3 class="mb-2">Gross estate value</h3>
                <h4>What is the current value of properties you own?</h4>
              </div>
              <div class="mt-6 mb-6">
                <AlternativeButton
                  :title="'Add Property'"
                  @gradientButtonClicked="showPropertyDialog = true"
                />
              </div>
              <div
                v-for="(property, index) in testator.properties"
                :key="`property-market-value-${index}`"
                no-gutters
              >
                <PropertyMarketValue
                  v-model="propertyValues[index]"
                  :property="property"
                />
              </div>

              <v-row v-if="testator.properties.length" no-gutters class="my-5">
                <v-col cols="12" sm="8" md="9">
                  <h4 v-if="marriedInCommunityOrCustomary">
                    Total joint property value
                  </h4>
                  <h4 v-else>Total property value</h4>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <h4>R {{ totalPropertyValue | currency }}</h4>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <h4 v-if="marriedInCommunityOrCustomary" class="field-label">
                    Value of your and your spouse's assets, other than property?
                  </h4>
                  <h4 v-else class="field-label">
                    Value of your assets other than property?
                  </h4>
                  <p>
                    eg. investments, cars, art, jewelery, shares in business,
                    value of any life policies that
                    <u>pay out to your estate</u>, etc.
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  md="3"
                  :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }"
                >
                  <ValidationProvider
                    id="non_property_asset_value"
                    v-slot="{ errors }"
                    name="non_property_asset_value"
                    rules="required"
                  >
                    <TextField
                      v-model="currencyNonPropertyAssetValue"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      @keypress="isNumber($event)"
                      :errors="errors"
                      :name="'non_property_asset_value'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-divider />

              <v-divider />

              <v-row no-gutters class="my-4">
                <v-col cols="12" sm="8" md="9">
                  <h4 v-if="marriedInCommunityOrCustomary">
                    Gross value of joint estate
                  </h4>
                  <h4 v-else>Gross estate value</h4>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <h4>R {{ grossEstateValue | currency }}</h4>
                </v-col>
              </v-row>
            </div>

            <div class="insurance-asset-block">
              <h3>Deemed assets</h3>
              <v-row no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <h4 class="field-label">Value of life policies</h4>
                  <p>
                    Value of any life policies which have been left to
                    beneficiaries
                    <u>other than your spouse or estate.</u>
                    Key Man and Business Life policies <u>should not</u> be
                    included here.
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  md="3"
                  :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }"
                >
                  <ValidationProvider
                    id="deemed_assets_value"
                    v-slot="{ errors }"
                    name="deemed_assets_value"
                    rules="required"
                  >
                    <TextField
                      v-model="currencyDeemedAssetsValue"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      @keypress="isNumber($event)"
                      :errors="errors"
                      :name="'deemed_assets_value'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </div>

            <div
              v-if="testator.anyAssetsLeftToSpouse"
              class="insurance-asset-block"
            >
              <h3>Value of all assets left to your spouse</h3>
              <ValidationProvider
                v-if="!testator.anyAssetsLeftToNonSpouseBeneficiaries"
                id="all_assets_left_to_spouse"
                v-slot="{ errors }"
                name="all_assets_left_to_spouse"
                rules="required"
              >
                <RadioSelect
                  v-model="allAssetsLeftToSpouse"
                  :items="allAssetsLeftToSpouseOptions"
                  :group="'all_assets_left_to_spouse'"
                  :errors="errors"
                  :flex-column="true"
                />
              </ValidationProvider>
              <v-row v-if="allAssetsLeftToSpouse == false" no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <p class="field-label">
                    Total value of assets left to spouse
                  </p>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <ValidationProvider
                    id="asset_value_left_to_spouse"
                    v-slot="{ errors }"
                    name="asset_value_left_to_spouse"
                    :rules="{ required: true, max_currency: grossEstateValue }"
                  >
                    <TextField
                      v-model="currencyAssetValueLeftToSpouse"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      @keypress="isNumber($event)"
                      :errors="errors"
                      :name="'asset_value_left_to_spouse'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <p class="field-label">
                    Do you have a section 4A Estate Duty rollover as a result of
                    a spouse having predeceased you?
                  </p>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <ValidationProvider
                    id="has_estate_duty_rollover"
                    v-slot="{ errors }"
                    name="has_estate_duty_rollover"
                    rules="required"
                  >
                    <RadioSelect
                      v-model="hasEstateDutyRollover"
                      :items="estateDutyRolloverOptions"
                      :group="'has_estate_duty_rollover'"
                      :errors="errors"
                      :flex-column="true"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row v-if="hasEstateDutyRollover == 'YES'" no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <p class="field-label">
                    What is the rollover amount you are allowed to apply to your
                    estate?
                  </p>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <ValidationProvider
                    id="estate_duty_rollover_amount"
                    v-slot="{ errors }"
                    name="estate_duty_rollover_amount"
                    rules="required"
                  >
                    <TextField
                      v-model="currencyEstateDutyRolloverAmount"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      @keypress="isNumber($event)"
                      :errors="errors"
                      :name="'estate_duty_rollover_amount'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
            </div>

            <div class="insurance-asset-block">
              <v-row no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <h3 class="field-label">Capital Gains Tax</h3>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  md="3"
                  :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }"
                >
                  <ValidationProvider
                    id="capital_gains_tax_amount"
                    name="capital_gains_tax_amount"
                  >
                    <TextField
                      v-model="currencyCapitalGainsTaxAmount"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      :name="'capital_gains_tax_amount'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12">
                  <p>
                    <strong>How CGT is Calculated:</strong><br />
                    Capital Gain = (Sale Price - Purchase Price - Improvement
                    Costs) Tax is only paid on a percentage of the gain (40% for
                    individuals, 80% for trusts & companies)
                  </p>
                </v-col>
                <v-col cols="12" class="mt-5">
                  <GradientButton
                    :title="'Calculate Capital Gains Tax'"
                    :fill="false"
                    @gradientButtonClicked="calculateCapitalGainsTax"
                  />
                </v-col>
              </v-row>
            </div>

            <div class="insurance-asset-block">
              <h3>Liabilities in the estate</h3>
              <div
                v-for="(property, index) in testator.properties"
                :key="`property-debt-${index}`"
                no-gutters
              >
                <PropertyOutstandingDebt
                  v-model="propertyDebtValues[index]"
                  :property="property"
                />
              </div>
              <div
                v-for="(vehicle, index) in testator.vehicles"
                :key="`vehicle-debt-${index}`"
                no-gutters
              >
                <VehicleOutstandingDebt
                  v-model="vehicleDebtValues[index]"
                  :vehicle="vehicle"
                />
              </div>
              <v-row no-gutters>
                <v-col cols="12" sm="8" md="9">
                  <h4 v-if="marriedInCommunityOrCustomary" class="field-label">
                    Total other liabilities/debt in your and your spouse's
                    estate
                  </h4>
                  <h4 v-else class="field-label">
                    Total other liabilities/debt in your estate
                  </h4>
                  <p>
                    The amount of debt you have, exclusive of mortgage and hire
                    purchase debt
                  </p>
                </v-col>
                <v-col
                  cols="12"
                  sm="4"
                  md="3"
                  :class="{ 'mt-6': $vuetify.breakpoint.smAndDown }"
                >
                  <ValidationProvider
                    id="other_liabilities"
                    v-slot="{ errors }"
                    name="other_liabilities"
                    rules="required"
                  >
                    <TextField
                      v-model="currencyOtherLiabilities"
                      :prefix="'R'"
                      :pattern="'[0-9\s]*'"
                      @keypress="isNumber($event)"
                      :errors="errors"
                      :name="'other_liabilities'"
                    />
                  </ValidationProvider>
                </v-col>
              </v-row>

              <v-row no-gutters class="my-4">
                <v-col cols="12" sm="8" md="9">
                  <h4 v-if="marriedInCommunityOrCustomary">
                    Total liabilities of the joint estate
                  </h4>
                  <h4 v-else>Total liabilities</h4>
                </v-col>
                <v-col cols="12" sm="4" md="3">
                  <h4>R {{ totalLiabilities | currency }}</h4>
                </v-col>
              </v-row>
            </div>
          </ValidationObserver>
        </div>

        <div class="input-button-container">
          <GradientButton
            :title="nextButtonText"
            :small="true"
            :callout="'right'"
            :loading="buttonLoading"
            @gradientButtonClicked="submit"
          />
        </div>
      </v-col>
    </v-row>

    <PropertyDialog v-model="showPropertyDialog" :testator="testator" />
    <CapitalGainsTaxDialog
      v-model="showCapitalGainsTaxDialog"
      @taxCalculated="updateCapitalGainsTaxAmount"
    />
  </v-container>
</template>

<script>
  import PropertyDialog from '../assets/PropertyDialog';
  import CapitalGainsTaxDialog from '../guest/calculators/capital_gains_tax/CapitalGainsTaxDialog.vue';
  import { UPDATE_PROPERTY_MUTATION } from '../../constants/graphql/properties';
  import { UPDATE_ESTATE_COST_CALCULATION_MUTATION } from '../../constants/graphql/estate-cost-calculations';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import PropertyMarketValue from './PropertyMarketValue.vue';
  import {
    formatCurrency,
    parseCurrency,
    isNumberCheck,
  } from '../../constants/js/currency-input';
  import PropertyOutstandingDebt from './PropertyOutstandingDebt.vue';
  import VehicleOutstandingDebt from './VehicleOutstandingDebt.vue';
  import { UPDATE_VEHICLE_MUTATION } from '../../constants/graphql/vehicles';

  export default {
    name: 'EstimatedInsuranceRequiredInput',
    components: {
      PropertyDialog,
      PropertyMarketValue,
      PropertyOutstandingDebt,
      VehicleOutstandingDebt,
      CapitalGainsTaxDialog,
    },
    props: {
      testator: {
        required: true,
        type: Object,
      },
    },
    data() {
      return {
        showPropertyDialog: false,
        showCapitalGainsTaxDialog: false,
        propertyValues: [],
        nonPropertyAssetValue: null,
        allAssetsLeftToSpouse: null,
        assetValueLeftToSpouse: null,
        hasEstateDutyRollover: null,
        estateDutyRolloverAmount: null,
        otherLiabilities: null,
        deemedAssetsValue: null,
        buttonLoading: false,
        propertyDebtValues: [],
        vehicleDebtValues: [],
        capitalGainsTaxAmount: null,
        estateDutyRolloverOptions: [
          { text: 'Yes', value: 'YES' },
          { text: 'No', value: 'NO' },
          { text: "I don't know", value: 'I_DONT_KNOW' },
        ],
        allAssetsLeftToSpouseOptions: [
          { text: 'I am leaving all my assets to my spouse', value: true },
          { text: 'I am leaving some of my assets to my spouse', value: false },
        ],
      };
    },
    computed: {
      testatorAgeAndIdCorrect() {
        return (
          this.testator.age >= 18 &&
          this.testator.age <= 74 &&
          this.testator.identityNumber.idType !== 'PASSPORT'
        );
      },
      nextButtonText() {
        const isAdvisor = this.$store.getters.isAdvisor;
        const isLoggedInAsSomeoneElse =
          this.$store.getters.isLoggedInAsSomeoneElse;
        const hasAdvisorFullInsurance = this.testator.enabledFeatures.includes(
          'advisor_full_insurance'
        );
        const hasAdvisor = !!this.testator.advisor;
        const test = this.testator.advisor;
        const isCompanyDefaultAdvisor =
          hasAdvisor && this.testator.advisor.company_default_advisor;

        if (
          this.testatorAgeAndIdCorrect &&
          !this.testator.enabledFeatures.includes('liquidity_calculator') &&
          (!hasAdvisorFullInsurance ||
            (hasAdvisorFullInsurance &&
              ((isAdvisor && !isLoggedInAsSomeoneElse) ||
                (!isAdvisor && (!hasAdvisor || isCompanyDefaultAdvisor)))))
        ) {
          return 'Next';
        } else {
          return 'Calculate estate admin costs';
        }
      },
      totalPropertyValue() {
        var sum = 0;
        for (const [index, property] of this.testator.properties.entries()) {
          if (this.propertyValues[index]) {
            const marketValue = this.propertyValues[index]['marketValue'];
            const ownershipValue = this.propertyValues[index]['ownershipValue'];
            if (this.marriedInCommunityOrCustomary) {
              sum +=
                property.ownership == 'CO_OWN' ? ownershipValue : marketValue;
            } else {
              sum +=
                property.ownership == 'SOLE_OWN' ? marketValue : ownershipValue;
            }
          }
        }
        return sum;
      },
      totalPropertyDebt() {
        var sum = 0;
        for (const [index, property] of this.testator.properties.entries()) {
          if (this.propertyDebtValues[index]) {
            const outstandingDebt =
              this.propertyDebtValues[index]['outstandingDebt'];
            const ownershipDebtValue =
              this.propertyDebtValues[index]['ownershipDebtValue'];
            if (this.marriedInCommunityOrCustomary) {
              sum +=
                property.ownership == 'CO_OWN'
                  ? ownershipDebtValue
                  : outstandingDebt;
            } else {
              sum +=
                property.ownership == 'SOLE_OWN'
                  ? outstandingDebt
                  : ownershipDebtValue;
            }
          }
        }
        return sum;
      },
      totalVehicleDebt() {
        var sum = 0;
        for (const [index, vehicle] of this.testator.vehicles.entries()) {
          if (this.vehicleDebtValues[index]) {
            sum += this.vehicleDebtValues[index]['outstandingDebt'];
          }
        }
        return sum;
      },
      totalLiabilities() {
        return (
          this.totalPropertyDebt +
          this.totalVehicleDebt +
          +this.otherLiabilities
        );
      },
      grossEstateValue() {
        return this.totalPropertyValue + +this.nonPropertyAssetValue;
      },
      marriedInCommunityOrCustomary() {
        return (
          this.testator.maritalRegime == 'IN_COMMUNITY' ||
          this.testator.maritalRegime == 'CUSTOMARY'
        );
      },
      currencyNonPropertyAssetValue: {
        get: function () {
          return formatCurrency(this.nonPropertyAssetValue);
        },
        set: function (newValue) {
          this.nonPropertyAssetValue = parseCurrency(newValue);
        },
      },
      currencyAssetValueLeftToSpouse: {
        get: function () {
          return formatCurrency(this.assetValueLeftToSpouse);
        },
        set: function (newValue) {
          this.assetValueLeftToSpouse = parseCurrency(newValue);
        },
      },
      currencyDeemedAssetsValue: {
        get: function () {
          return formatCurrency(this.deemedAssetsValue);
        },
        set: function (newValue) {
          this.deemedAssetsValue = parseCurrency(newValue);
        },
      },
      currencyOtherLiabilities: {
        get: function () {
          return formatCurrency(this.otherLiabilities);
        },
        set: function (newValue) {
          this.otherLiabilities = parseCurrency(newValue);
        },
      },
      currencyEstateDutyRolloverAmount: {
        get: function () {
          return formatCurrency(this.estateDutyRolloverAmount);
        },
        set: function (newValue) {
          this.estateDutyRolloverAmount = parseCurrency(newValue);
        },
      },
      currencyCapitalGainsTaxAmount: {
        get: function () {
          return formatCurrency(this.capitalGainsTaxAmount);
        },
        set: function (newValue) {
          this.capitalGainsTaxAmount = parseCurrency(newValue);
        },
      },
    },
    created() {
      this.setExistingFields();
      if (this.testator.anyAssetsLeftToNonSpouseBeneficiaries) {
        this.allAssetsLeftToSpouse = false;
      }
    },
    methods: {
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            this.updateVehicles();
            if (this.testator.properties.length) {
              for (const [
                index,
                property,
              ] of this.testator.properties.entries()) {
                // Updates properties and then updates insurance after last property updated
                this.updateProperty(index, property);
              }
            } else {
              // estateCostCalculation is updated after properties if properties exist, to avoid race condition.
              // If no properties exist then it updates EstateCostCalculation here
              this.updateEstateCostCalculation();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      updateVehicles() {
        if (this.testator.vehicles.length) {
          for (const [index, vehicle] of this.testator.vehicles.entries()) {
            this.updateVehicle(index, vehicle);
          }
        }
      },
      updateProperty(index, property) {
        const propertyInputDetails = {
          marketValue: parseInt(this.propertyValues[index]['marketValue']),
          outstandingDebt: parseInt(
            this.propertyDebtValues[index]['outstandingDebt']
          ),
        };
        const id = property.id;
        this.$apollo
          .mutate({
            mutation: UPDATE_PROPERTY_MUTATION,
            variables: {
              id,
              propertyInputDetails,
            },
          })
          .then(_response => {
            // After all properties have been updated, update insurance to avoid race condition
            if (this.testator.properties.length == index + 1) {
              this.updateEstateCostCalculation();
            }
          })
          .catch(error => {
            this.buttonLoading = false;
            this.$emit('displayError');
            throw error;
          });
      },
      updateVehicle(index, vehicle) {
        const vehicleInputDetails = {
          outstandingDebt: parseInt(
            this.vehicleDebtValues[index]['outstandingDebt']
          ),
        };
        const id = vehicle.id;

        this.$apollo
          .mutate({
            mutation: UPDATE_VEHICLE_MUTATION,
            variables: {
              id,
              vehicleInputDetails,
            },
          })
          .catch(error => {
            this.buttonLoading = false;
            this.$emit('displayError');
            throw error;
          });
      },
      updateEstateCostCalculation() {
        const allAssetsLeftToSpouse = this.testator.anyAssetsLeftToSpouse
          ? this.allAssetsLeftToSpouse
          : null;
        const assetValueLeftToSpouse =
          allAssetsLeftToSpouse == false ? this.assetValueLeftToSpouse : null;
        const estateCostCalculationInputDetails = {
          testatorId: this.testatorId,
          totalPropertyValue: parseInt(this.totalPropertyValue),
          nonPropertyAssetValue: parseInt(this.nonPropertyAssetValue),
          allAssetsLeftToSpouse: allAssetsLeftToSpouse,
          assetValueLeftToSpouse: parseInt(assetValueLeftToSpouse),
          hasEstateDutyRollover: this.hasEstateDutyRollover,
          estateDutyRolloverAmount: parseInt(this.estateDutyRolloverAmount),
          otherLiabilities: parseInt(this.otherLiabilities),
          totalLiabilities: parseInt(this.totalLiabilities),
          deemedAssetsValue: parseInt(this.deemedAssetsValue),
          grossEstateValue: parseInt(this.grossEstateValue),
          capitalGainsTaxAmount: parseInt(this.capitalGainsTaxAmount),
        };
        this.$apollo
          .mutate({
            mutation: UPDATE_ESTATE_COST_CALCULATION_MUTATION,
            variables: {
              estateCostCalculationInputDetails,
            },
            update: (cache, { data: { updateEstateCostCalculation } }) => {
              this.updateCacheAfterUpdateEstateCostCalculation(
                cache,
                updateEstateCostCalculation
              );
            },
          })
          .then(_response => {
            const isAdvisor = this.$store.getters.isAdvisor;
            const isLoggedInAsSomeoneElse =
              this.$store.getters.isLoggedInAsSomeoneElse;
            const hasAdvisorFullInsurance =
              this.testator.enabledFeatures.includes('advisor_full_insurance');
            const hasAdvisor = !!this.testator.advisor;
            const isCompanyDefaultAdvisor =
              hasAdvisor && this.testator.advisor.companyDefaultAdvisor;

            if (
              this.testatorAgeAndIdCorrect &&
              !this.testator.enabledFeatures.includes('liquidity_calculator') &&
              (!hasAdvisorFullInsurance ||
                (hasAdvisorFullInsurance &&
                  ((isAdvisor && !isLoggedInAsSomeoneElse) ||
                    (!isAdvisor && (!hasAdvisor || isCompanyDefaultAdvisor)))))
            ) {
              this.$router.push({ name: 'GetFreeInsuranceQuote' });
            } else {
              this.$router.push({ name: 'EstimatedInsuranceRequiredOutput' });
            }
          })
          .catch(error => {
            this.$emit('displayError');
            this.buttonLoading = false;
            throw error;
          });
      },
      updateCacheAfterUpdateEstateCostCalculation(
        cache,
        updateEstateCostCalculation
      ) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);
        data.testator.estateCostCalculation = updateEstateCostCalculation;

        cache.writeQuery({ ...query, data: data });
      },
      setExistingFields() {
        // using native JSON functions removes reactivity
        // so we can clone an object/variable without mutating the original source
        if (this.testator.estateCostCalculation) {
          this.nonPropertyAssetValue = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.nonPropertyAssetValue
            )
          );
          this.allAssetsLeftToSpouse = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.allAssetsLeftToSpouse
            )
          );
          this.assetValueLeftToSpouse = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.assetValueLeftToSpouse
            )
          );
          this.hasEstateDutyRollover = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.hasEstateDutyRollover
            )
          );
          this.estateDutyRolloverAmount = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.estateDutyRolloverAmount
            )
          );
          this.otherLiabilities = JSON.parse(
            JSON.stringify(this.testator.estateCostCalculation.otherLiabilities)
          );
          this.deemedAssetsValue = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.deemedAssetsValue
            )
          );
          this.capitalGainsTaxAmount = JSON.parse(
            JSON.stringify(
              this.testator.estateCostCalculation.capitalGainsTaxAmount
            )
          );
        }

        for (const [index, property] of this.testator.properties.entries()) {
          this.propertyValues[index] = {
            marketValue: JSON.parse(JSON.stringify(property.marketValue)),
          };
          this.propertyDebtValues[index] = {
            outstandingDebt: JSON.parse(
              JSON.stringify(property.outstandingDebt)
            ),
          };
        }
        for (const [index, vehicle] of this.testator.vehicles.entries()) {
          this.vehicleDebtValues[index] = {
            outstandingDebt: JSON.parse(
              JSON.stringify(vehicle.outstandingDebt)
            ),
          };
        }
      },
      isNumber(event) {
        isNumberCheck(event);
      },
      calculateCapitalGainsTax() {
        this.showCapitalGainsTaxDialog = true;
      },
      updateCapitalGainsTaxAmount(value) {
        this.capitalGainsTaxAmount = value;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .insurance-assets-and-liabilities-container {
    .title-section {
      font-size: 32px !important;
      font-weight: 700 !important;
      line-height: 36px !important;
      color: #282828 !important;
      margin-top: 5px;
      margin-bottom: 20px !important;
    }

    .input-button-container {
      padding-left: 60px;
    }

    .left-section-container {
      padding-left: 60px;
    }

    .insurance-asset-block {
      // border in global.scss because of IE issue
      border-radius: 0;
      padding: 15px 20px;
      margin-bottom: 20px;
    }

    .field-label {
      margin-top: 18px;
    }

    @media screen and (max-width: 600px) {
      .insurance-asset-block {
        p {
          margin-bottom: 0;
        }
      }
    }
  }
  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .insurance-assets-and-liabilities-container {
      .left-section-container {
        padding-left: 0;
      }
      .input-button-container {
        padding-left: 0;
      }
    }
  }

  .capital-gains-tax-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
</style>
